import { io } from 'socket.io-client'
import { getStoredToken, fetchFreshToken } from './tokenManager'
import { BASE_URL } from '../api'

export function createSocketClient() {
  let socket = null
  let isRefreshing = false

  // Track joined rooms so we can rejoin automatically
  let joinedIncidents = new Set()

  function connect() {
    // If socket already exists and is connected, return it
    if (socket && socket.connected) {
      return socket
    }

    const token = getStoredToken()

    socket = io(BASE_URL, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000
    })

    // -----------------------
    // SOCKET LIFECYCLE LOGS
    // -----------------------
    socket.on('connect', () => {})

    socket.on('disconnect', (reason) => {
      console.warn('🟠 WS disconnected:', reason)
    })

    socket.on('connect_error', (err) => {
      console.error('🔴 WS connect_error:', err.message)
    })

    socket.on('reconnect_attempt', (attempt) => {})

    socket.on('reconnect', (attempt) => {
      // Rejoin rooms after reconnect
      rejoinRooms()
    })

    socket.on('reconnect_failed', () => {
      console.error('❌ WS reconnection failed')
    })

    // -----------------------
    // AUTH / TOKEN HANDLING
    // -----------------------
    socket.on('authenticated', () => {})

    socket.on('unauthorized', async (msg) => {
      console.error('❌ Token rejected:', msg)

      if (isRefreshing) {
        return
      }

      isRefreshing = true

      try {
        const newToken = await fetchFreshToken()

        // Update auth
        socket.auth = { token: newToken }

        // Force reconnect with new token
        socket.connect()
      } catch (err) {
        console.error('❌ Token refresh failed:', err)
      } finally {
        isRefreshing = false
      }
    })

    return socket
  }

  function disconnect() {
    if (socket) {
      socket.disconnect()
      socket = null
      joinedIncidents.clear()
    }
  }

  // -----------------------
  // JOIN INCIDENT ROOM
  // -----------------------
  function joinIncident(incidentId) {
    if (!socket || !socket.connected) {
      console.warn('⚠️ Socket not connected, cannot join incident')
      return
    }

    // Already joined this incident
    if (joinedIncidents.has(incidentId)) {
      return
    }

    joinedIncidents.add(incidentId)

    socket.emit('join_incident', incidentId, (ack) => {
      if (ack?.success) {
      } else {
        console.warn(`⚠️ Failed to join incident room: ${incidentId}`, ack)
        joinedIncidents.delete(incidentId) // Remove from set if failed
      }
    })
  }

  function leaveIncident(incidentId) {
    if (!socket || !socket.connected) {
      return
    }

    joinedIncidents.delete(incidentId)

    socket.emit('leave_incident', incidentId, (ack) => {
      if (ack?.success) {
      } else {
        console.warn(`⚠️ Failed to leave incident room: ${incidentId}`, ack)
      }
    })
  }

  // Rejoin all rooms after reconnect
  function rejoinRooms() {
    if (!socket || !socket.connected) return

    joinedIncidents.forEach((id) => {
      socket.emit('join_incident', id, (ack) => {
        if (ack?.success) {
        } else {
          console.warn(`⚠️ Failed to rejoin room: ${id}`, ack)
        }
      })
    })
  }

  // -----------------------
  // TRACKING EVENTS
  // -----------------------
  function onTrackUpdate(callback) {
    if (!socket) return

    socket.on('add_points_track', (data) => {
      callback(data)
    })
  }

  function onTrackCancelled(callback) {
    if (!socket) return

    socket.on('cancel_track', (data) => {
      callback(data)
    })
  }

  function onPinCreated(callback) {
    if (!socket) return

    socket.on('add_pin_track', (data) => {
      callback(data)
    })
  }

  function onPinRemoved(callback) {
    if (!socket) return

    socket.on('remove_pin_track', (data) => {
      callback(data)
    })
  }

  function onPinUpdated(callback) {
    if (!socket) return

    socket.on('update_pin_track', (data) => {
      callback(data)
    })
  }

  function onTrackRemoved(callback) {
    if (!socket) return

    socket.on('remove_track', (data) => {
      callback(data)
    })
  }

  // -----------------------
  // EMIT EVENTS
  // -----------------------
  function emitCancelTrack(data) {
    if (!socket || !socket.connected) {
      console.warn('⚠️ Socket not connected, cannot emit cancel_track')
      return false
    }

    socket.emit('cancel_track', data, (ack) => {
      if (ack?.success) {
        console.log('✅ Cancel track event emitted successfully')
      } else {
        console.warn('⚠️ Cancel track event failed:', ack)
      }
    })

    return true
  }

  // -----------------------
  // CLEANUP
  // -----------------------
  function removeAllTrackingListeners() {
    if (!socket) return

    socket.off('add_points_track')
    socket.off('cancel_track')
    socket.off('add_pin_track')
    socket.off('remove_pin_track')
    socket.off('update_pin_track')
    socket.off('remove_track')
  }

  function getConnectionStatus() {
    return socket ? socket.connected : false
  }

  return {
    connect,
    disconnect,
    joinIncident,
    leaveIncident,
    onTrackUpdate,
    onTrackCancelled,
    onPinCreated,
    onPinRemoved,
    onPinUpdated,
    onTrackRemoved,
    emitCancelTrack,
    removeAllTrackingListeners,
    removeTrackUpdateListener: () => socket?.off('add_points_track'),
    getConnectionStatus
  }
}
